import Mention from "@tiptap/extension-mention";
import { CharacterCount, Placeholder } from "@tiptap/extensions";
import type { ParseOptions } from "@tiptap/pm/model";
import { Editor, EditorContent, EditorContext, Extension, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { SuggestionOptions } from "@tiptap/suggestion";
import React, { useEffect, useImperativeHandle, useMemo } from "react";

import { faXmarkCircle } from "@performyard/fontawesome/pro-solid-svg-icons";

import { ButtonIcon } from "../../atoms/ButtonIcon";
import { useInputField } from "../InputWrapper/InputContext";
import "./TextEditor.css";
import { RedTextAfterLimit } from "./extensions/RedTextAfterLimit";

/**
 * Helper to extract text from editor with consistent line break handling
 */
const getEditorText = (editor: Editor | null | undefined, blockSeparator: string): string => {
	return editor?.getText({ blockSeparator }) || "";
};

type ExtendedSuggestionOptions = Partial<SuggestionOptions> & {
	parseContent?: (text: string) => { type: string; attrs?: Record<string, unknown>; text?: string }[];
};

export type TextEditorRef = {
	/**
	 * The TipTap editor instance
	 */
	editor: Editor | null;
	/**
	 * Focus the editor
	 */
	focus: () => void;
	/**
	 * Get the text content of the editor
	 */
	getText: () => string;
	/**
	 * Set the content of the editor
	 */
	setContent: (content: string) => void;
	/**
	 * Clear the editor content
	 */
	clear: () => void;
};

export type TextEditorProps = {
	/**
	 * A string of class names to be applied to the editor container
	 */
	dangerouslySetClassName?: string;
	/**
	 * The id of the editor
	 */
	id: string;
	/**
	 * The initial value of the editor
	 */
	initialValue: string;
	/**
	 * The callback function that is called when the editor's content changes
	 */
	onChange: (value: string) => void;
	/**
	 * The callback function that is called when the editor loses focus
	 */
	onBlur?: (event: FocusEvent) => void;
	/**
	 * Whether the editor is disabled
	 */
	disabled?: boolean;
	/**
	 * The minimum height of the editor in pixels. The editor has a base minimum of 32px (matching InputBase).
	 * Use this to enforce a larger minimum height. The editor grows automatically as content is added.
	 */
	minHeight?: number;
	/**
	 * The maximum height of the editor
	 */
	maxHeight?: number;
	/**
	 * The character limit of the editor
	 */
	characterLimit?: number;
	/**
	 * The suggestions of the editor
	 */
	suggestions?: ExtendedSuggestionOptions[];
	/**
	 * Whether the editor is read-only
	 */
	readOnly?: boolean;
	/**
	 * Whether the editor is in an error state
	 */
	errored?: boolean;
	/**
	 * The placeholder of the editor
	 */
	placeholder?: string;
	/**
	 * Whether to show a clear button (X icon) on the right side of the editor.
	 * When clicked, clears the editor content and refocuses the editor.
	 * The clear button appears before the rightActionElement if both are provided.
	 * Icons are positioned in the top-right corner, aligned with the first line of text.
	 *
	 * @default false
	 */
	clearable?: boolean;
	/**
	 * Element to display on the right side of the editor, after the editor content.
	 * Useful for action buttons, additional icons, or custom controls.
	 * The rightActionElement appears after the clear button (if clearable is enabled).
	 * Icons are positioned in the top-right corner, aligned with the first line of text.
	 *
	 * @example <ButtonIcon icon={faSearch} onClick={handleSearch} />
	 */
	rightActionElement?: React.ReactNode;
	/**
	 * Ref to access the editor instance and methods
	 */
	ref?: React.Ref<TextEditorRef>;
	/**
	 * The parse options for the editor
	 */
	parseOptions?: ParseOptions;
	/**
	 * The separator to use between block elements (like paragraphs) when extracting text.
	 * TipTap's default is "\n\n" (double newlines), but "\n" (single newlines) is often more intuitive.
	 * @default "\n" (single line breaks)
	 */
	blockSeparator?: string;
};

/**
 * A rich text editor component built with TipTap, featuring character limits, suggestions/mentions, and various states (disabled, read-only, errored).
 * @example
 * const editorRef = useRef<TextEditorRef>(null);
 *
 * <TextEditor
 * 	ref={editorRef}
 * 	id="editor"
 * 	initialValue="Hello, world!"
 * 	onChange={handleChange}
 * 	onBlur={handleBlur}
 * />
 *
 * // Access editor methods
 * editorRef.current?.focus();
 * editorRef.current?.getText();
 *
 * @param props - The props for the TextEditor component
 * @returns The TextEditor component
 */

export const TextEditor = ({
	dangerouslySetClassName,
	id,
	initialValue,
	onChange,
	onBlur,
	disabled,
	minHeight = 100,
	maxHeight = 300,
	characterLimit,
	suggestions = [],
	readOnly,
	errored,
	placeholder,
	clearable = false,
	rightActionElement,
	ref,
	parseOptions = {},
	blockSeparator = "\n",
}: TextEditorProps) => {
	const editorContentRef = React.useRef<HTMLDivElement>(null);
	// Use input context to get info from the nearest parent InputWrapper
	const { id: contextId, error, disabled: contextDisabled } = useInputField();
	const generatedId = React.useId();
	// Generate a unique id unless one is provided through props or context
	const finalId = id || contextId || generatedId;
	// Check if the editor has an error from context or props
	const hasError = !!error || errored;
	// Use disabled state from context or props
	const isDisabled = disabled || contextDisabled;
	const extensions = useMemo(() => {
		const baseExtensions: Extension[] = [
			StarterKit,
			CharacterCount.configure(),
			Placeholder.configure({
				placeholder: placeholder || "Start typing...",
				emptyEditorClass: "is-editor-empty",
			}),
			Mention.configure({
				HTMLAttributes: {
					class: "mention-inline bg-primary-1 text-primary-6 px-1 py-0.5 rounded text-sm font-medium",
					"data-type": "mention",
				},
				suggestions: suggestions?.length > 0 ? [...suggestions] : [],
			}) as Extension,
		];

		if (characterLimit !== undefined) {
			baseExtensions.push(
				RedTextAfterLimit.configure({
					limit: characterLimit,
				})
			);
		}

		return baseExtensions;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [characterLimit, placeholder, JSON.stringify(suggestions)]);

	// Parse content using suggestion parsers
	const parseContentWithSuggestions = React.useCallback(
		(text: string) => {
			let content: { type: string; attrs?: Record<string, unknown>; text?: string }[] = [{ type: "text", text }];

			// Apply each suggestion parser
			suggestions.forEach((suggestion) => {
				if (suggestion.parseContent) {
					const newContent: { type: string; attrs?: Record<string, unknown>; text?: string }[] = [];
					content.forEach((part) => {
						if (part.type === "text" && part.text) {
							const parsed = suggestion.parseContent!(part.text);
							newContent.push(...parsed);
						} else {
							newContent.push(part);
						}
					});
					content = newContent;
				}
			});

			return {
				type: "doc",
				content: [
					{
						type: "paragraph",
						content: content.length > 0 ? content : [{ type: "text", text }],
					},
				],
			};
		},
		[suggestions]
	);

	const editorMaxHeight = `${maxHeight - 20}px`;

	const editor = useEditor(
		{
			extensions,
			content: initialValue,
			parseOptions: {
				...parseOptions,
			},
			onUpdate: ({ editor }: { editor: Editor }) => {
				onChange(getEditorText(editor, blockSeparator));
			},
			onBlur: ({ event }: { event: FocusEvent }) => {
				onBlur?.(event);
			},
			editorProps: {
				attributes: {
					id: finalId,
					class: `tiptap overflow-y-auto overflow-x-hidden paragraph-3 font-regular [&_.ProseMirror]:paragraph-3 [&_.ProseMirror]:font-regular [&_.ProseMirror]:leading-[1.5] [&_.ProseMirror]:pb-0 [&_.ProseMirror]:break-words [&_.ProseMirror]:overflow-wrap-anywhere [&_.ProseMirror]:max-w-full focus:outline-none focus:ring-0`,
					style: `max-height: ${editorMaxHeight}; line-height: 1.5;`,
				},
			},
		},
		[extensions, onBlur, onChange, editorMaxHeight, blockSeparator]
	);

	// Expose editor methods through ref
	useImperativeHandle(
		ref,
		() => ({
			editor,
			focus: () => {
				editor?.commands.focus();
			},
			getText: () => {
				return getEditorText(editor, blockSeparator);
			},
			setContent: (content: string) => {
				const hasParser = suggestions.some((s) => s.parseContent);
				if (hasParser) {
					const parsedContent = parseContentWithSuggestions(content);
					editor?.commands.setContent(parsedContent);
				} else {
					editor?.commands.setContent(content);
				}
			},
			clear: () => {
				editor?.commands.clearContent();
			},
		}),
		[editor, suggestions, parseContentWithSuggestions, blockSeparator]
	);

	useEffect(() => {
		if (editor) {
			// Disable editing when disabled OR readOnly, but readOnly still allows focusing
			editor.setEditable(!isDisabled && !readOnly);
		}
	}, [readOnly, isDisabled, editor]);

	const { charactersCount } = useEditorState({
		editor,
		selector: (context: {
			editor: { storage: { characterCount: { characters: () => number; words: () => number } } };
		}) => ({
			charactersCount: context.editor.storage.characterCount.characters(),
			wordsCount: context.editor.storage.characterCount.words(),
		}),
	});

	const editorText = useEditorState({
		editor,
		selector: (context: { editor: Editor }) => getEditorText(context.editor, blockSeparator),
	});

	const handleClear = () => {
		onChange("");
		editor?.commands.focus();
	};

	// Update editor content when value prop changes
	useEffect(() => {
		if (editor && getEditorText(editor, blockSeparator) !== initialValue) {
			// Check if any suggestions have parsers
			const hasParser = suggestions.some((s) => s.parseContent);
			if (hasParser) {
				const parsedContent = parseContentWithSuggestions(initialValue);
				editor.commands.setContent(parsedContent);
			} else {
				editor.commands.setContent(initialValue);
			}
		}
	}, [editor, initialValue, suggestions, parseContentWithSuggestions, blockSeparator]);

	// Update editor class when character count changes
	useEffect(() => {
		if (characterLimit === undefined) {
			return;
		}

		const editorElement = editorContentRef.current?.closest(".border-solid");
		if (!editorElement) {
			return;
		}

		if (charactersCount > characterLimit) {
			editorElement.classList.add("character-limit-exceeded");
		} else {
			editorElement.classList.remove("character-limit-exceeded");
		}
	}, [charactersCount, characterLimit]);

	// Handle label clicks to focus the editor
	useEffect(() => {
		if (editor && finalId) {
			const handleLabelClick = (event: Event) => {
				const target = event.target as HTMLElement;
				// Check if the clicked element is a label associated with this editor
				// Look for both 'for' attribute and 'htmlFor' property
				const isAssociatedLabel =
					target.tagName === "LABEL" &&
					(target.getAttribute("for") === finalId || (target as HTMLLabelElement).htmlFor === finalId);

				if (isAssociatedLabel) {
					event.preventDefault();
					editor.commands.focus();
				}
			};

			// Listen for clicks on labels that might be associated with this editor
			document.addEventListener("click", handleLabelClick);

			return () => {
				document.removeEventListener("click", handleLabelClick);
			};
		}
	}, [editor, finalId]);

	// Memoize the provider value to avoid unnecessary re-renders
	const providerValue = useMemo(() => ({ editor }), [editor]);

	return (
		<EditorContext.Provider value={providerValue}>
			<div
				aria-readonly={readOnly}
				aria-disabled={isDisabled}
				{...(readOnly && { "data-readonly": true })}
				{...(isDisabled && { "data-disabled": true })}
				{...(hasError && { "data-errored": true })}
				className={`box-border flex min-h-8 w-full min-w-0 max-w-full shrink items-start justify-start gap-2 overflow-hidden rounded-1 border border-solid border-neutral-3 bg-neutral-0 px-2 text-paragraph-3 font-regular text-neutral-10 focus-within:border-neutral-5 data-[disabled]:cursor-not-allowed data-[errored]:border-red-6 data-[readonly]:bg-neutral-1 data-[errored]:text-red-5 data-[disabled]:opacity-disabled ${dangerouslySetClassName}`}
				style={{
					minHeight: minHeight ? `${minHeight}px` : undefined,
					maxHeight: `${maxHeight}px`,
				}}
			>
				<div ref={editorContentRef} className="min-w-0 flex-1 overflow-hidden py-1">
					<EditorContent
						editor={editor}
						disabled={isDisabled}
						readOnly={readOnly}
						tabIndex={isDisabled ? -1 : readOnly ? 0 : undefined}
					/>
				</div>
				{(clearable && editorText) || rightActionElement ? (
					<div className="mt-1.5 flex shrink-0 items-center gap-1 self-start">
						{clearable && editorText && (
							<ButtonIcon
								icon={faXmarkCircle}
								size={16}
								color="neutral"
								shade={3}
								onClick={handleClear}
								disabled={isDisabled}
								aria-disabled={isDisabled}
								data-testid={`${finalId}-clear-button`}
								aria-label="Clear editor"
							/>
						)}
						{rightActionElement && rightActionElement}
					</div>
				) : null}
				{characterLimit !== undefined && (
					// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
					<div
						className={`mt-1.5 shrink-0 cursor-text self-start text-right text-paragraph-3 font-regular ${charactersCount > characterLimit ? "text-red-5" : "text-neutral-7"}`}
						onClick={() => {
							if (!isDisabled && editor) {
								editor.commands.focus();
							}
						}}
					>
						{charactersCount} / {characterLimit}
					</div>
				)}
			</div>
		</EditorContext.Provider>
	);
};
TextEditor.displayName = "TextEditor";

export default TextEditor;
